import type { Cents } from "../domain/money";
import { formatCents } from "../domain/money";
import type { PurchaseAllocation, PurchaseGraph, PurchaseLine } from "../domain/purchase";

/**
 * Deterministic purchase arithmetic (Phase 5).
 *
 * Order of operations (approved):
 *   line gross − line discount            = line net basis
 *   distribute purchase-level discount    (proportional to net basis)
 *   = line net (allocation availability & taxable basis)
 *   distribute purchase tax               (explicit per-line tax honored;
 *                                          remainder over taxable derived lines,
 *                                          proportional to line net)
 *   allocation tax  = line tax proportional to allocation amount
 *   shipping        = explicit allocation overrides first, remainder
 *                     proportional to allocated amounts (purchase-wide)
 *
 * All distributions use LARGEST-REMAINDER rounding so cents always sum
 * back exactly to the authoritative receipt figures. Everything is
 * reproducible from stored inputs and explainable.
 */

/**
 * Distribute `total` across `weights` proportionally, in whole cents,
 * with largest-remainder rounding. Σ result === total (when Σ weights > 0).
 * Works for negative totals/weights (returns) by operating on magnitudes.
 */
export function distributeProportionally(total: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];
  const sign = total < 0 ? -1 : 1;
  const absTotal = Math.abs(total);
  const absWeights = weights.map((w) => Math.abs(w));
  const weightSum = absWeights.reduce((s, w) => s + w, 0);
  if (weightSum === 0 || absTotal === 0) return new Array<number>(n).fill(0);

  const exact = absWeights.map((w) => (absTotal * w) / weightSum);
  const floors = exact.map((x) => Math.floor(x));
  let remainder = absTotal - floors.reduce((s, x) => s + x, 0);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const result = [...floors];
  for (let k = 0; k < order.length && remainder > 0; k++) {
    result[order[k].i] += 1;
    remainder -= 1;
  }
  return result.map((r) => r * sign);
}

export type TaxSource = "explicit" | "derived" | "none";

export interface LineCalculation {
  lineId: string;
  description: string;
  grossCents: Cents;
  lineDiscountCents: Cents;
  netBasisCents: Cents;
  purchaseDiscountShareCents: Cents;
  /** Available for allocation; taxable basis. */
  netCents: Cents;
  taxCents: Cents;
  taxSource: TaxSource;
  allocatedCents: Cents;
  unallocatedCents: Cents;
  /** Tax attributable to the unallocated portion. */
  unallocatedTaxCents: Cents;
  status: AllocationStatus;
  explanation: string[];
}

export interface AllocationCalculation {
  allocationId: string;
  lineId: string;
  projectId: string;
  amountCents: Cents;
  taxCents: Cents;
  shippingCents: Cents;
  /** amount + tax + shipping — the project's actual cost from this allocation. */
  totalCents: Cents;
  explanation: string[];
}

export type AllocationStatus = "unallocated" | "partial" | "full";

export interface PurchaseCalculation {
  purchaseId: string;
  lines: LineCalculation[];
  allocations: AllocationCalculation[];
  /** Σ line gross. */
  linesGrossCents: Cents;
  /** subtotal − Σ line gross (receipt lines not yet itemized). */
  unitemizedCents: Cents;
  /** Σ line net (allocation availability). */
  availableCents: Cents;
  allocatedCents: Cents;
  unallocatedCents: Cents;
  /** Shipping not yet attributed to any allocation. */
  unallocatedShippingCents: Cents;
  /** True when Σ line tax === purchase tax (explicit lines may break this). */
  taxReconciles: boolean;
  headerReconciles: boolean;
  status: AllocationStatus;
}

function statusFor(available: number, allocated: number, extraUnallocated = 0): AllocationStatus {
  const remaining = Math.abs(available) - Math.abs(allocated) + Math.abs(extraUnallocated);
  if (Math.abs(allocated) === 0) return "unallocated"; // nothing allocated (incl. empty purchases)
  if (remaining <= 0) return "full";
  return "partial";
}

export function computePurchase(graph: PurchaseGraph): PurchaseCalculation {
  const { purchase, lines, allocations } = graph;
  const sorted = [...lines].sort((a, b) => a.sortOrder - b.sortOrder);

  // 1. Line net basis and purchase-level discount distribution.
  const netBasis = sorted.map((l) => l.grossCents - l.lineDiscountCents);
  const discountShares = distributeProportionally(purchase.discountCents, netBasis);
  const nets = netBasis.map((b, i) => b - discountShares[i]);

  // 2. Tax: explicit lines honored; remainder over taxable derived lines.
  const explicitTotal = sorted.reduce(
    (s, l) => s + (l.taxable && l.explicitTaxCents !== null ? l.explicitTaxCents : 0),
    0,
  );
  const derivedIdx = sorted
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.taxable && l.explicitTaxCents === null)
    .map(({ i }) => i);
  const remainingTax = purchase.taxCents - explicitTotal;
  const derivedShares = distributeProportionally(
    remainingTax,
    derivedIdx.map((i) => nets[i]),
  );
  const lineTax = sorted.map(() => 0);
  const lineTaxSource: TaxSource[] = sorted.map(() => "none");
  sorted.forEach((l, i) => {
    if (l.taxable && l.explicitTaxCents !== null) {
      lineTax[i] = l.explicitTaxCents;
      lineTaxSource[i] = "explicit";
    }
  });
  derivedIdx.forEach((i, k) => {
    lineTax[i] = derivedShares[k];
    lineTaxSource[i] = "derived";
  });
  const taxReconciles = lineTax.reduce((s, t) => s + t, 0) === purchase.taxCents;

  // 3. Allocations: tax within line; shipping purchase-wide.
  const allocsByLine = new Map<string, PurchaseAllocation[]>();
  for (const a of allocations) {
    const arr = allocsByLine.get(a.lineId) ?? [];
    arr.push(a);
    allocsByLine.set(a.lineId, arr);
  }
  const allocTax = new Map<string, number>();
  const lineCalcs: LineCalculation[] = sorted.map((l, i) => {
    const allocs = allocsByLine.get(l.id) ?? [];
    const allocated = allocs.reduce((s, a) => s + a.amountCents, 0);
    const unallocated = nets[i] - allocated;
    // Tax proportional to amounts INCLUDING the unallocated remainder so the
    // line tax always sums exactly across allocations + unallocated.
    const derived = allocs.filter(a=>a.taxOverrideCents==null);
    const explicitTax = allocs.reduce((sum,a)=>sum+(a.taxOverrideCents??0),0);
    const shares = distributeProportionally(lineTax[i]-explicitTax, [
      ...derived.map((a) => a.amountCents),
      unallocated,
    ]);
    derived.forEach((a,k)=>allocTax.set(a.id,shares[k]));
    allocs.filter(a=>a.taxOverrideCents!=null).forEach(a=>allocTax.set(a.id,a.taxOverrideCents!));
    const unallocatedTax = shares[shares.length - 1] ?? 0;
    const explanation = [
      `Line amount ${formatCents(l.grossCents)}` +
        (l.lineDiscountCents !== 0 ? ` − line discount ${formatCents(l.lineDiscountCents)} = ${formatCents(netBasis[i])}` : ""),
    ];
    if (discountShares[i] !== 0) {
      explanation.push(
        `− share of purchase discount ${formatCents(discountShares[i])} (proportional to line basis) = net ${formatCents(nets[i])}`,
      );
    } else {
      explanation.push(`Net ${formatCents(nets[i])}`);
    }
    if (!l.taxable) explanation.push("Non-taxable line: excluded from tax distribution");
    else if (lineTaxSource[i] === "explicit") explanation.push(`Tax ${formatCents(lineTax[i])} (explicit from receipt)`);
    else explanation.push(`Tax ${formatCents(lineTax[i])} (receipt tax distributed proportionally to taxable net)`);
    return {
      lineId: l.id,
      description: l.description,
      grossCents: l.grossCents,
      lineDiscountCents: l.lineDiscountCents,
      netBasisCents: netBasis[i],
      purchaseDiscountShareCents: discountShares[i],
      netCents: nets[i],
      taxCents: lineTax[i],
      taxSource: lineTaxSource[i],
      allocatedCents: allocated,
      unallocatedCents: unallocated,
      unallocatedTaxCents: unallocatedTax,
      status: statusFor(nets[i], allocated),
      explanation,
    };
  });

  // Shipping: explicit overrides first; remainder proportional to allocated amounts.
  const overrideTotal = allocations.reduce((s, a) => s + (a.shippingOverrideCents ?? 0), 0);
  const nonOverride = allocations.filter((a) => a.shippingOverrideCents === null);
  const shippingRemaining = purchase.shippingCents - overrideTotal;
  const shippingShares = distributeProportionally(
    shippingRemaining,
    nonOverride.map((a) => a.amountCents),
  );
  const allocShipping = new Map<string, number>();
  allocations.forEach((a) => {
    if (a.shippingOverrideCents !== null) allocShipping.set(a.id, a.shippingOverrideCents);
  });
  nonOverride.forEach((a, k) => allocShipping.set(a.id, shippingShares[k]));
  const shippingAttributed = allocations.reduce((s, a) => s + (allocShipping.get(a.id) ?? 0), 0);

  const allocCalcs: AllocationCalculation[] = allocations.map((a) => {
    const line = lineCalcs.find((l) => l.lineId === a.lineId);
    const tax = allocTax.get(a.id) ?? 0;
    const shipping = allocShipping.get(a.id) ?? 0;
    const explanation = [
      `Allocated ${formatCents(a.amountCents)} of line “${line?.description ?? ""}” (net ${formatCents(line?.netCents ?? 0)})`,
      `Tax share ${formatCents(tax)} (line tax ${formatCents(line?.taxCents ?? 0)} proportional to allocated amount)`,
    ];
    if (shipping !== 0) {
      explanation.push(
        a.shippingOverrideCents !== null
          ? `Shipping ${formatCents(shipping)} (explicit assignment)`
          : `Shipping ${formatCents(shipping)} (purchase shipping proportional to allocated value)`,
      );
    }
    explanation.push(`Actual cost to project: ${formatCents(a.amountCents + tax + shipping)}`);
    return {
      allocationId: a.id,
      lineId: a.lineId,
      projectId: a.projectId,
      amountCents: a.amountCents,
      taxCents: tax,
      shippingCents: shipping,
      totalCents: a.amountCents + tax + shipping,
      explanation,
    };
  });

  const linesGross = sorted.reduce((s, l) => s + l.grossCents, 0);
  const unitemized = purchase.subtotalCents - linesGross;
  const available = nets.reduce((s, n) => s + n, 0);
  const allocated = allocations.reduce((s, a) => s + a.amountCents, 0);
  const headerReconciles =
    purchase.subtotalCents - purchase.discountCents + purchase.taxCents + purchase.shippingCents ===
    purchase.totalCents;

  return {
    purchaseId: purchase.id,
    lines: lineCalcs,
    allocations: allocCalcs,
    linesGrossCents: linesGross,
    unitemizedCents: unitemized,
    availableCents: available,
    allocatedCents: allocated,
    unallocatedCents: available - allocated + unitemized,
    unallocatedShippingCents: purchase.shippingCents - shippingAttributed,
    taxReconciles,
    headerReconciles,
    status: statusFor(available, allocated, unitemized),
  };
}

/** Would adding/changing an allocation over-allocate its line? */
export function lineAvailableCents(graph: PurchaseGraph, lineId: string): Cents {
  const calc = computePurchase(graph).lines.find((l) => l.lineId === lineId);
  return calc ? calc.netCents : 0;
}

export function lineOverAllocated(line: Pick<PurchaseLine, "id">, netCents: Cents, allocatedCents: Cents): boolean {
  void line;
  if (netCents >= 0) return allocatedCents > netCents || allocatedCents < 0;
  return allocatedCents < netCents || allocatedCents > 0;
}
